import { Dayjs } from 'dayjs';
import classNames from 'classnames';
import type { DateComponent, GetProps } from './DateSelector';

// Generate all days between minDate and maxDate for list view
const generateDaysList = (rangeStart: Dayjs, rangeEnd: Dayjs): Dayjs[] => {
  const days: Dayjs[] = [];
  let current = rangeStart.startOf('day');
  const end = rangeEnd.startOf('day');
  while (current.isBefore(end) || current.isSame(end, 'day')) {
    days.push(current);
    current = current.add(1, 'day');
  }
  return days;
};

// Extended props interface for our date list panel
interface DateListPanelProps extends GetProps<DateComponent> {
  minDate?: Dayjs;
  maxDate?: Dayjs;
  highlightedDays?: {
    dates: string[];
    legend?: string;
  };
}

// Standard React component with extended props
export default function DateListPanel({
  value,
  onSelect,
  minDate,
  maxDate,
  highlightedDays,
}: DateListPanelProps) {
  if (!minDate || !maxDate) {
    return null;
  }

  // Generate all days in the range
  const daysList = generateDaysList(minDate, maxDate);

  // Format date in full format (e.g., "Monday, 15 January 2024")
  const formatFullDate = (date: Dayjs) => date.format('dddd, D MMMM YYYY');

  // Check if a date is highlighted
  const isHighlighted = (date: Dayjs) => {
    if (!highlightedDays?.dates) return false;
    return highlightedDays.dates.includes(date.format('YYYY-MM-DD'));
  };

  // Check if a date is selected
  const isSelected = (date: Dayjs) => {
    if (!value) return false;
    return date.isSame(value, 'day');
  };

  return (
    <div className="DateListPanel">
      {daysList.map((date) => {
        const selected = isSelected(date);
        const highlighted = isHighlighted(date);
        return (
          <button
            type="button"
            className={classNames({
              button: true,
              selected,
              highlighted,
            })}
            onClick={() => {
              onSelect(date);
            }}
          >
            <span className="date">{formatFullDate(date)}</span>
          </button>
        );
      })}
    </div>
  );
}
